/**
 * Image Cache Service
 *
 * Implements browser-side image caching using:
 * 1. IndexedDB for storing image blobs
 * 2. Cache API for HTTP caching
 * 3. Memory cache for instant access
 * 4. Prefetching for upcoming images
 */

const DB_NAME = 'btc-image-cache';
const DB_VERSION = 1;
const STORE_NAME = 'images';
const CACHE_NAME = 'btc-images-v1';
const MAX_CACHE_SIZE_MB = 100; // Maximum cache size in MB
const MAX_CACHE_AGE_DAYS = 7; // How long to keep cached images

class ImageCacheService {
  constructor() {
    this.db = null;
    this.memoryCache = new Map(); // In-memory cache for instant access
    this.prefetchQueue = [];
    this.isInitialized = false;
  }

  /**
   * Initialize the cache service
   */
  async init() {
    if (this.isInitialized) return;

    try {
      await this.initIndexedDB();
      await this.cleanOldCache();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize image cache:', error);
    }
  }

  /**
   * Initialize IndexedDB
   */
  initIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('size', 'size', { unique: false });
        }
      };
    });
  }

  /**
   * Get image from cache (checks memory → IndexedDB → Cache API → Network)
   */
  async getImage(url) {
    if (!url) return null;

    // 1. Check memory cache first (fastest)
    if (this.memoryCache.has(url)) {
      return this.memoryCache.get(url);
    }

    // 2. Check IndexedDB
    try {
      const cached = await this.getFromIndexedDB(url);
      if (cached && !this.isExpired(cached.timestamp)) {
        // Store in memory for faster next access
        this.memoryCache.set(url, cached.blob);
        return cached.blob;
      }
    } catch (error) {
      console.warn('IndexedDB retrieval failed:', error);
    }

    // 3. Check Cache API
    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(url);
      if (response) {
        const blob = await response.blob();

        // Store in memory and IndexedDB for next time
        this.memoryCache.set(url, blob);
        await this.saveToIndexedDB(url, blob);
        return blob;
      }
    } catch (error) {
      console.warn('Cache API retrieval failed:', error);
    }

    // 4. Fetch from network and cache
    return await this.fetchAndCache(url);
  }

  /**
   * Fetch image from network and cache it
   */
  async fetchAndCache(url) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.clone().blob();

      // Cache in parallel (don't wait)
      this.cacheImage(url, blob, response.clone());

      return blob;
    } catch (error) {
      console.error('Failed to fetch image:', error);
      return null;
    }
  }

  /**
   * Cache image in all storage layers
   */
  async cacheImage(url, blob, response = null) {
    try {
      // 1. Memory cache
      this.memoryCache.set(url, blob);

      // 2. IndexedDB
      await this.saveToIndexedDB(url, blob);

      // 3. Cache API (if response provided)
      if (response) {
        const cache = await caches.open(CACHE_NAME);
        await cache.put(url, response);
      }

    } catch (error) {
      console.error('Failed to cache image:', error);
    }
  }

  /**
   * Save image to IndexedDB
   */
  saveToIndexedDB(url, blob) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const data = {
        url,
        blob,
        timestamp: Date.now(),
        size: blob.size
      };

      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get image from IndexedDB
   */
  getFromIndexedDB(url) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(url);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Prefetch images for upcoming banners
   */
  async prefetchImages(imageUrls) {
    if (!Array.isArray(imageUrls) || imageUrls.length === 0) return;

    // Filter out already cached images
    const urlsToPrefetch = [];
    for (const url of imageUrls) {
      if (!this.memoryCache.has(url)) {
        const cached = await this.getFromIndexedDB(url).catch(() => null);
        if (!cached || this.isExpired(cached.timestamp)) {
          urlsToPrefetch.push(url);
        }
      }
    }

    // Prefetch in background (low priority)
    urlsToPrefetch.forEach((url, index) => {
      setTimeout(() => {
        this.fetchAndCache(url).catch(err =>
          console.warn('Prefetch failed for:', url, err)
        );
      }, index * 500); // Stagger requests by 500ms (faster than videos)
    });

    if (urlsToPrefetch.length > 0) {
    }
  }

  /**
   * Check if cached item is expired
   */
  isExpired(timestamp) {
    const ageInDays = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
    return ageInDays > MAX_CACHE_AGE_DAYS;
  }

  /**
   * Clean old cache entries
   */
  async cleanOldCache() {
    try {
      if (!this.db) return;

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');

      const cutoffTime = Date.now() - (MAX_CACHE_AGE_DAYS * 24 * 60 * 60 * 1000);
      const range = IDBKeyRange.upperBound(cutoffTime);

      let deletedCount = 0;
      const request = index.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else if (deletedCount > 0) {
        }
      };

      // Also clean Cache API
      const cache = await caches.open(CACHE_NAME);
      const requests = await cache.keys();

      for (const request of requests) {
        const response = await cache.match(request);
        const dateHeader = response.headers.get('date');
        if (dateHeader) {
          const age = (Date.now() - new Date(dateHeader).getTime()) / (1000 * 60 * 60 * 24);
          if (age > MAX_CACHE_AGE_DAYS) {
            await cache.delete(request);
          }
        }
      }
    } catch (error) {
      console.error('Failed to clean old cache:', error);
    }
  }

  /**
   * Get cache size and stats
   */
  async getCacheStats() {
    try {
      const stats = {
        memoryCache: this.memoryCache.size,
        indexedDB: 0,
        totalSize: 0
      };

      if (this.db) {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const countRequest = store.count();

        await new Promise((resolve) => {
          countRequest.onsuccess = () => {
            stats.indexedDB = countRequest.result;
            resolve();
          };
        });

        // Get total size
        const allRequest = store.getAll();
        await new Promise((resolve) => {
          allRequest.onsuccess = () => {
            stats.totalSize = allRequest.result.reduce((sum, item) => sum + (item.size || 0), 0);
            stats.totalSizeMB = (stats.totalSize / (1024 * 1024)).toFixed(2);
            resolve();
          };
        });
      }

      return stats;
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return null;
    }
  }

  /**
   * Clear all caches
   */
  async clearCache() {
    try {
      // Clear memory cache
      this.memoryCache.clear();

      // Clear IndexedDB
      if (this.db) {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await store.clear();
      }

      // Clear Cache API
      await caches.delete(CACHE_NAME);

    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  /**
   * Create object URL from cached image
   */
  async getCachedImageURL(url) {
    const blob = await this.getImage(url);
    if (blob) {
      return URL.createObjectURL(blob);
    }
    return url; // Fallback to original URL
  }
}

// Export singleton instance
export const imageCacheService = new ImageCacheService();
export default imageCacheService;
